import React from 'react';
import { cn } from '../../utils/cn';
import { formatPnlParts } from '../../utils/positionDisplay';
import { useHidePnl } from '../../hooks/useHidePnl';

type FlashDirection = 'up' | 'down' | null;

const FLASH_DURATION_MS = 800;

const FLASH_BG: Record<NonNullable<FlashDirection>, string> = {
  up:   'bg-green-100 dark:bg-green-900/40',
  down: 'bg-red-100 dark:bg-red-900/40',
};

/**
 * Diff prev-vs-current value and return a transient `'up' | 'down'` flash
 * direction that auto-clears after FLASH_DURATION_MS. No flash on first
 * render or when the value transitions to/from null (initial load + entry
 * pending → filled shouldn't visually pop).
 */
function usePnlFlash(value: number | null | undefined): FlashDirection {
  const prevRef = React.useRef<number | null | undefined>(value);
  const [flash, setFlash] = React.useState<FlashDirection>(null);

  React.useEffect(() => {
    const prev = prevRef.current;
    const bothNumeric = typeof value === 'number' && typeof prev === 'number';
    if (bothNumeric && value !== prev) {
      setFlash(value > (prev as number) ? 'up' : 'down');
      const t = setTimeout(() => setFlash(null), FLASH_DURATION_MS);
      prevRef.current = value;
      return () => clearTimeout(t);
    }
    prevRef.current = value;
  }, [value]);

  return flash;
}

export interface PnlFlashProps {
  value: number | null | undefined;
  /** Extra classes applied to the wrapper span (typography, density). */
  className?: string;
  /**
   * When true (default), applies the directional green/red text color from
   * formatPnlParts. Set false when the parent already controls the text
   * color (e.g. the white-on-emerald ProHeroBanner).
   */
  colorize?: boolean;
}

/**
 * Live-P&L display that briefly flashes the cell background on value change:
 *   - +800ms green flash when value increases
 *   - +800ms red flash when value decreases
 *
 * Safe to use everywhere P&L is displayed. When the value never changes
 * between renders (closed P&L, snapshot dialogs), the component is a visual
 * no-op identical to `formatPnl()` / `formatPnlValue()`. Null/undefined
 * renders as the canonical "—" placeholder.
 *
 * Why a custom component: the schedule + dashboard pages poll every 30s and
 * the per-position P&L is recomputed live via LTP lookup on the backend.
 * Without a visual cue, traders can't tell if numbers moved between ticks
 * — they have to read each value and remember the previous one. The flash
 * matches the Zerodha Kite / Sensibull convention so users get immediate
 * direction feedback without scanning.
 */
export const PnlFlash: React.FC<PnlFlashProps> = ({ value, className, colorize = true }) => {
  const flash = usePnlFlash(value);
  const [hidden] = useHidePnl();
  const { text, className: pnlColor } = formatPnlParts(value ?? null);
  // Hidden: neutral masked text — no green/red or flash that would leak direction.
  return (
    <span
      className={cn(
        'tabular-nums transition-colors duration-500 rounded px-0.5',
        hidden ? 'text-gray-400 dark:text-gray-500' : colorize && pnlColor,
        !hidden && flash && FLASH_BG[flash],
        className,
      )}
    >
      {hidden ? '••••' : text}
    </span>
  );
};

export default PnlFlash;
